import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LoginOutlined,
    LogoutOutlined,
    UserOutlined,
    HeartOutlined,
    PhoneOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import { BadgesContext, BadgesProvider } from "../../context/BadgesContext";
import { ModalContext } from "../../context/ModalContext";
import { Badge, Drawer } from "antd";
import { IconMenu2 } from "@tabler/icons-react";

import logoWhite from "../../assets/images/lomahuuto-fi-logo-white-1024.png";
import LoginModal from "../modals/LoginModal";
import api from "../../services/Api";
import FavoritesDrawer from "./FavoritesDrawer";

const Header = ({ pageType }) => {
    const { isAuthenticated, logout, login } = useContext(AuthContext);
    const badges = useContext(BadgesContext);
    const { showLoginModal, isLoginModalOpen, hideLoginModal } = useContext(ModalContext);
    const [sticky, setSticky] = useState(false);
    const [favoritesDrawerOpen, setFavoritesDrawerOpen] = useState(false);
    const [valikkoAuki, setValikkoAuki] = useState(false);
    const navigate = useNavigate();

    /* Luonnostili (Mika 30.8.2026): myynnin voi aloittaa ilman rekisteroitymista.
       Taustalla syntyy piilotili ja sama lomake aukeaa suoraan. Jos aloitus
       epaonnistuu, pudotaan vanhaan kirjautumispolkuun. */
    const aloitaMyynti = async (e) => {
        if (isAuthenticated) return;
        e.preventDefault();
        try {
            const vastaus = await api.aloitaLuonnos();
            await login(vastaus.token);
            navigate("/oma-sivu/ilmoitukset/uusi");
        } catch (err) {
            navigate("/kirjaudu");
        }
    };

    const suljeValikko = () => {
        setValikkoAuki(false);
    };

    // Badges are now provided by AuthContext; no polling here

    const showFavoritesDrawer = () => {
        setFavoritesDrawerOpen(true);
    };

    const onClose = () => {
        setFavoritesDrawerOpen(false);
    };

    const stickyClass = [
        "text-white",
        "shadow-md",
        "shadow-black/30",
        "bg-primary",
        "bg-opacity-100",
    ];

    const headerClass = [
        "sticky",
        "top-0",
        "text-black",
        "transition-colors",
        "duration-300",
        "ease-in-out",
        "px-5",
        "py-4",
        "md:py-4",
        "z-40",
        "bg-primary",
        pageType === 0 ? "sticky w-full bg-black" : "bg-primary bg-opacity-100",
    ];

    // Function to apply dynamic classes based on sticky or pageType
    const linkClass = (isSticky) =>
        isSticky || pageType === 1
            ? "flex cursor-pointer text-white hover:text-white text-base hover:border-b-2 hover:border-b-white hover:pb-1 transition-all duration-100 ease-in-out"
            : "flex cursor-pointer text-white hover:text-white text-base hover:border-b-2 hover:border-b-black hover:pb-1 transition-all duration-100 ease-in-out";

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 250) {
                setSticky(true);
            }

            if (window.scrollY < 100) {
                setSticky(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <BadgesProvider>
            <header
                className={sticky ? `${headerClass.join(" ")} ${stickyClass.join(" ")}` : `${headerClass.join(" ")}`}
            >
                <div
                    className={
                        "container m-auto max-w-screen-2xl flex flex-wrap items-center justify-between text-base"
                    }
                >
                    <Link to="/">
                        <img
                            // src={pageType === 1 || sticky ? logoWhite : logoDark}
                            src={logoWhite}
                            alt="Lomahuuto.fi"
                            className="w-44 sm:w-60"
                            width="1024"
                            height="149"
                        />
                    </Link>

                    <Link
                        to="tel:+358503753594"
                        className="flex items-center space-x-2 text-white hover:text-white active:text-white"
                    >
                        <PhoneOutlined />
                        <span className="hidden sm:inline">+358 50 375 3594</span>
                    </Link>

                    {/* Desktop Menu (md and larger) */}
                    <div className="hidden lg:flex items-center space-x-4 h-10">
                        {pageType !== 0 && (
                            <Link
                                to={isAuthenticated ? "/oma-sivu/ilmoitukset/uusi" : "/kirjaudu"}
                        onClick={aloitaMyynti}
                                className="py-1 px-4 inline-block md:ml-4 bg-white text-primary text-base uppercase rounded-xl hover:bg-green-50 hover:text-black transition-all cursor-pointer"
                            >
                                <div>Myy tai vuokraa osakkeesi</div>
                            </Link>
                        )}
                        <Link to="/ehdot-ja-ohjeet" className={linkClass(sticky)}>
                            Ehdot ja ohjeet
                        </Link>
                        <Link to="/yhteystiedot" className={linkClass(sticky)}>
                            Yhteystiedot
                        </Link>
                        <span className={linkClass(sticky)}>|</span>

                        {isAuthenticated ? (
                            <>
                                <Badge count={badges.auctions} size="small" color="blue" offset={[4, 0]}>
                                    <Link className={linkClass(sticky)} to="/oma-sivu">
                                        <UserOutlined className="mr-1" />
                                        Oma tili
                                    </Link>
                                </Badge>
                                <Badge count={badges.favorites} size="small" color="blue" offset={[4, 0]}>
                                    <div className={linkClass(sticky)} onClick={showFavoritesDrawer}>
                                        <HeartOutlined className="mr-1" />
                                        Suosikit
                                    </div>
                                </Badge>
                                <Link className={linkClass(sticky)} onClick={logout}>
                                    <LogoutOutlined className="mr-1" />
                                    Kirjaudu ulos
                                </Link>
                                <FavoritesDrawer open={favoritesDrawerOpen} onClose={onClose} />
                            </>
                        ) : (
                            <div onClick={() => showLoginModal("login")} className={linkClass(sticky)}>
                                <LoginOutlined className="mr-2 text-lg md:text-base" />
                                <span className="hidden md:block">Kirjaudu</span>
                            </div>
                        )}
                    </div>
                    {/* Valikkopainike alle 1024 px:n naytoille (Mika 25.8.2026).
                        Desktop-valikko nakyy vasta lg:sta ylospain ja alapalkki
                        vain alle 640 px (sm:hidden) - valiin jai aukko 640-1023 px,
                        jossa ei ollut kirjautumista eika valikkoa lainkaan.
                        Juuri siihen osuu Foldin avattu naytto. */}
                    <button
                        type="button"
                        aria-label="Valikko"
                        onClick={() => setValikkoAuki(true)}
                        className="lg:hidden flex items-center justify-center w-10 h-10 text-white"
                    >
                        <IconMenu2 />
                    </button>
                </div>

                <Drawer
                    title="Valikko"
                    placement="right"
                    onClose={suljeValikko}
                    open={valikkoAuki}
                    width={300}
                    zIndex={60}
                >
                    <div className="flex flex-col space-y-4 text-base">
                        <Link
                            to={isAuthenticated ? "/oma-sivu/ilmoitukset/uusi" : "/kirjaudu"}
                        onClick={aloitaMyynti}
                            onClick={suljeValikko}
                            className="py-2 px-4 text-center bg-primary text-white uppercase rounded-xl hover:text-white"
                        >
                            Myy tai vuokraa osakkeesi
                        </Link>

                        <Link to="/myydaan-lomaosake" onClick={suljeValikko}>
                            Myytävät lomaosakkeet
                        </Link>
                        <Link to="/vuokrataan-lomaosake" onClick={suljeValikko}>
                            Vuokrattavat lomaosakkeet
                        </Link>
                        <Link to="/artikkelit" onClick={suljeValikko}>
                            Artikkelit
                        </Link>

                        <hr />

                        <Link to="/ehdot-ja-ohjeet" onClick={suljeValikko}>
                            Ehdot ja ohjeet
                        </Link>
                        <Link to="/yhteystiedot" onClick={suljeValikko}>
                            Yhteystiedot
                        </Link>
                        <div className="flex items-center gap-2">
                            <PhoneOutlined />
                            <Link to="tel:+358503753594">+358 50 375 3594</Link>
                        </div>

                        <hr />

                        {isAuthenticated ? (
                            <>
                                <Link to="/oma-sivu" onClick={suljeValikko} className="flex items-center gap-2">
                                    <UserOutlined /> Oma tili
                                </Link>
                                <div
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => {
                                        suljeValikko();
                                        showFavoritesDrawer();
                                    }}
                                >
                                    <HeartOutlined /> Suosikit
                                </div>
                                <div
                                    className="flex items-center gap-2 text-red-600 cursor-pointer"
                                    onClick={() => {
                                        suljeValikko();
                                        logout();
                                    }}
                                >
                                    <LogoutOutlined /> Kirjaudu ulos
                                </div>
                            </>
                        ) : (
                            <div
                                className="flex items-center gap-2 cursor-pointer"
                                onClick={() => {
                                    suljeValikko();
                                    showLoginModal("login");
                                }}
                            >
                                <LoginOutlined /> Kirjaudu
                            </div>
                        )}
                    </div>
                </Drawer>

                <LoginModal isOpen={isLoginModalOpen} onClose={hideLoginModal} />
            </header>
    </BadgesProvider>
    );
};

export default Header;
